import { Parser } from '@json2csv/plainjs'
import {
  ArrowPathIcon,
  Box,
  Flex,
  IconButton,
  Menu,
  NewspaperIcon,
  SparklesIcon,
  TableColumn,
  Text,
  Tooltip,
} from 'components'
import { useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { FullMetadata } from 'firebase/storage'
import { DataType } from 'ka-table'
import { isEqual, startCase, uniq } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GetLeadSessionsProps,
  HelperLeadProperty,
  IEvent,
  Lead,
  LeadSession,
  LeadSessionsFetchProps,
  LeadTemperature,
  SessionMetric,
  SessionTrait,
  SessionWithLead,
  SmartProperty,
  Sort,
  leadProperties,
  leadTemperatures,
  primaryLeadProperties,
} from 'types'
import {
  getAggregations,
  getLeadSession,
  getLeadSessions,
  getStorageUrlsInPath,
  splitDBProps,
  useUser,
} from 'utils'
import { getOtgMemberSessionFilters, useOnTheGo } from '.'
import { LeadTableSessionMediaPreview } from '../components'
import LeadTempIcon from '../components/event/LeadTempIcon'
import { useEvent } from './eventUtils'

export const optionalVisibleColSpecs = ['company', 'title', 'followUpGroup', 'notes', 'hasMedia']

export const mobileDefSessionFields = ['temp', 'name', 'created']

export interface FlatSession
  extends
    Omit<LeadSession, 'traits' | 'lead' | 'metrics'>,
    Omit<Lead, 'custom' | 'smartProperties'> {
  duration?: number
  moment?: string
  source: string
  [key: string]: any
}

export function flattenSession(session: SessionWithLead, event?: IEvent): FlatSession {
  const { lead, traits, metrics, ...rest } = session
  const [{ id: leadId }, { custom, smartProperties, ...leadRest }] = splitDBProps(lead) || {}
  const duration = getSessionDuration(metrics)
  const source = getSessionSource(session.appName, session.appTitle)
  const traitMap = createTraitMap(traits)
  const moment = getMomentTitle(event, session)
  const smartMap = getSmartPropertyMap(smartProperties)
  return {
    ...rest,
    ...leadRest,
    ...custom,
    ...smartMap,
    ...traitMap,
    source,
    duration,
    moment,
    leadId,
  }
}

export const getSessionDuration = (metrics?: SessionMetric[]) =>
  getDefaultMetrics(metrics)?.reduce((acc, cur) => acc + (cur.duration ?? 0), 0) || 0

export const getDefaultMetrics = (metrics?: SessionMetric[]) =>
  metrics?.filter(m => m.groupTitle === '_default') || []

export const getActionMetrics = (metrics?: SessionMetric[]) =>
  metrics?.filter(m => m.groupTitle === '_action') || []

export const getSessionSource = (appName: string, appTitle?: string) =>
  startCase(appName === 'custom' ? appTitle || 'Custom' : appName)

export const getMomentTitle = (event?: IEvent, session?: LeadSession, momentId?: number) =>
  event?.moments.find(m => m.id === (momentId || session?.momentId || 0))?.title

export const getMomentId = (event?: IEvent, momentTitle?: string) =>
  event?.moments.find(m => m.title === momentTitle)?.id || 0

export const getSmartPropertyMap = (smartProperties?: SmartProperty[]) =>
  smartProperties?.reduce(
    (acc, prop) => ({ ...acc, [prop.name]: prop.text || String(prop.number) || '' }),
    {} as Record<string, string>
  )

export function createTraitMap(traits?: SessionTrait[]) {
  let fieldMap: { [group: string]: string } = {}
  if (!traits) return fieldMap

  const groupTitles = uniq(traits.map(t => t.groupTitle || '')).filter(Boolean)

  groupTitles.forEach(groupTitle => {
    if (typeof groupTitle === 'string') {
      const titlesArray: string[] = traits
        .filter((trait: SessionTrait) => trait.groupTitle === groupTitle)
        .map((trait: SessionTrait) => trait.title)

      fieldMap[groupTitle] = titlesArray.join(', ')
    }
  })

  return fieldMap
}

interface ExportSessionsProps {
  event?: IEvent
  workspaceId: string
}

export async function exportSessions({ event, workspaceId }: ExportSessionsProps) {
  const { id: eventId, sessionCount = 0, title = '' } = event ?? {}

  const body: GetLeadSessionsProps = {
    eventId,
    limit: sessionCount + 10,
    sort: defaultSort,
    workspaceId,
  }

  try {
    const sessions = await getLeadSessions({ ...body, populateLead: true })
    const cleaned = sessions.data.map(s => {
      let cleaned = hideColumns(flattenSession(s, event ?? undefined), 'export')
      cleaned.created = dayjs(cleaned.created).format('YYYY-MM-DD HH:mm:ss')
      return Object.entries(cleaned).reduce((acc, [key, value]) => {
        acc[startCase(key)] = value
        return acc
      }, {} as any)
    })
    const parser = new Parser()
    const csv = encodeURIComponent(parser.parse(cleaned))
    downloadCSV(csv, title)
  } catch (err) {
    console.error(err)
  }
}

export function hideColumns(
  flatSession: Partial<FlatSession>,
  display: 'table' | 'panel' | 'export'
): Record<string, any> {
  const {
    apiDataStatus,
    appName,
    badgeNumber,
    createdBy,
    crmSync,
    eventId,
    firstName,
    id,
    lastName,
    leadId,
    metrics,
    name,
    smartPropertyFailure,
    temperature,
    templateId,
    traits,
    updated,
    updatedBy,
    workspaceId,

    ...rest
  } = flatSession

  switch (display) {
    case 'table':
      return { ...rest, name }
    case 'panel':
    case 'export':
      return { ...rest, firstName, lastName }
  }
}

function downloadCSV(fileContents: string, name: string) {
  var dataStr = 'data:text/csv;charset=utf-8,' + fileContents
  var downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  const time = dayjs().format('YYYY.MM.DD_hh.mma')
  const title = `${name}_${time}.csv`
  downloadAnchorNode.setAttribute('download', title)
  document.body.appendChild(downloadAnchorNode)
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

const defaultSort: Sort = { by: 'created', order: 'DESC' }

export function useLeadSessions(props: LeadSessionsFetchProps) {
  const { search, sort = defaultSort, offset, limit } = props
  const { event, filters: eventFilters = [] } = useEvent()
  const { workspace } = useWorkspace()
  const {
    filters: otgFilters = [],
    memberId: otgMemberId = '',
    sessionCount: otgSessionCount = 0,
    sessionCountLoading: otgSessionCountLoading = false,
  } = useOnTheGo()
  const filters = event ? eventFilters : [...getOtgMemberSessionFilters(otgMemberId), ...otgFilters]

  const skipInitialOtgFetch =
    !event && !otgSessionCountLoading && otgSessionCount === 0 && otgFilters.length === 0

  const [sessions, setSessions] = useState<SessionWithLead[]>([])
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading')

  const cachedBody = useRef<GetLeadSessionsProps<true>>(null)
  const body: GetLeadSessionsProps<true> = {
    eventId: event?.id,
    limit,
    offset,
    populateLead: true,
    search,
    sort,
    workspaceId: workspace?.id,
    filters,
  }
  const { lastSessionId, sessionCount: eventSessionCount = 0 } = event || {}
  const sessionCount = event ? eventSessionCount : otgSessionCount
  const sessionCountLoading = Boolean(event) ? false : otgSessionCountLoading
  const hasAllSessions = event
    ? sessions.length >= sessionCount
    : sessionCountLoading
      ? false
      : sessionCount === 0
        ? true
        : sessions.length >= sessionCount
  if (hasAllSessions) cachedBody.current = body
  const hasFetched = isEqual(cachedBody.current, body)

  useEffect(() => {
    if (event) return
    setSessions([])
    cachedBody.current = null
  }, [event, otgFilters, otgMemberId, workspace?.id])

  useEffect(() => {
    if (event) {
      if (!sessionCount) return setStatus('success')
      if (hasFetched) return
      getSessions()
      return
    }
    if (!workspace?.id || !otgMemberId) return
    if (skipInitialOtgFetch) return setStatus('success')
    if (otgSessionCountLoading) return
    if (hasFetched) return
    getSessions()
  })

  useEffect(() => {
    if (!event) return
    if (!lastSessionId || status !== 'success') return

    getLeadSession({ sessionId: lastSessionId })
      .then(res => mergeSessions([res.data]))
      .catch(e => {
        console.error('Error fetching latest session:', e)
        setStatus('error')
      })
  }, [lastSessionId])

  async function getSessions() {
    try {
      cachedBody.current = body
      setStatus('loading')
      const res = await getLeadSessions(body)
      if (body.offset === 0) setSessions(res.data)
      else mergeSessions(res.data)
      setStatus('success')
    } catch (e) {
      console.error('Error fetching lead sessions:', e)
      setStatus('error')
    }
  }

  function mergeSessions(newSessions: SessionWithLead[]) {
    setSessions(s => {
      const newSessionIds = newSessions.map(s => s.id)
      const existingSessions = s.filter(s => !newSessionIds.includes(s.id))
      const mergedSessions = [...existingSessions, ...newSessions]

      return mergedSessions.sort((a, b) => {
        let aValue: any, bValue: any

        if (leadProperties.includes(sort.by as keyof Lead)) {
          aValue = a.lead?.[sort.by as keyof Lead]
          bValue = b.lead?.[sort.by as keyof Lead]
        } else {
          aValue = a[sort.by as keyof SessionWithLead]
          bValue = b[sort.by as keyof SessionWithLead]
        }

        if (aValue === bValue) return 0
        if (aValue == null) return 1
        if (bValue == null) return -1

        const comparison = aValue < bValue ? -1 : 1
        return sort.order === 'ASC' ? comparison : -comparison
      })
    })
  }

  const updateSessions = useCallback((body: Array<SessionWithLead>) => {
    setSessions(s => {
      const newSessions = body.filter(n => !s.some(o => o.id === n.id))
      const updated = s.map(s => {
        const match = body.find(n => n.id === s.id)
        return { ...s, ...match }
      })
      return [...updated, ...newSessions]
    })
  }, [])

  const removeSessions = useCallback((ids: string[]) => {
    setSessions(s => s.filter(s => !ids.includes(s.id)))
  }, [])

  const _status = hasFetched ? status : 'loading'

  return [sessions, _status, hasAllSessions, updateSessions, removeSessions] as const
}

export type LeadSessionChange = LeadSessionUpdate | LeadSessionDelete

export interface LeadSessionUpdate {
  type: 'update'
  body: Partial<SessionWithLead> & { id: string }
}

export interface LeadSessionDelete {
  type: 'delete'
  body: { id: string }
}

const helperColumns: HelperLeadProperty[] = ['followUp', 'name', 'notes', 'optIn', 'temp']

export interface LeadTableColumn extends TableColumn {
  source: 'helper' | 'primary' | 'session' | 'smart' | 'trait' | 'custom'
  alwaysSelected?: boolean
}

interface MakeColumnsProps {
  workspaceId: string
  event?: IEvent
  onColumnUpdate?: (updatedLead?: Lead & { id: string }) => void
}

export async function makeColumns({
  workspaceId,
  event,
  onColumnUpdate,
}: MakeColumnsProps): Promise<LeadTableColumn[]> {
  const { id: eventId, leadDefaults } = event ?? {}
  const aggregations = [
    { table: 'sessionTraits' as const, values: ['_count'], categories: ['groupTitle'] },
    { table: 'smartProperties' as const, values: ['_count'], categories: ['name'] },
    { table: 'leadCustomKeys' as const, values: ['_count'], categories: ['key'] },
  ]
  const res = await getAggregations({
    aggregations,
    ...(eventId ? { eventIds: [eventId] } : {}),
    workspaceIds: [workspaceId],
  })
  const [traitAgg, smartPropAgg, customKeyAgg] = res.data

  const columns: LeadTableColumn[] = [
    { key: 'created', source: 'primary' },
    ...(eventId ? [{ key: 'momentId', source: 'session' as const, title: 'Moment' }] : []),
    { key: 'deviceName', source: 'session' },
    { key: 'endMethod', source: 'session' },
    { key: 'duration', source: 'session', isSortable: false },
    { key: 'appName', source: 'session', title: 'Source' },
    { key: 'mediaStatus', source: 'session', isSortable: false },
    { key: 'followUpOwner', source: 'helper', isSortable: true },
    ...primaryLeadProperties.map(key => ({ key, source: 'primary' as const })),
    ...helperColumns.map(key => ({ key, source: 'helper' as const })),
    ...(smartPropAgg?.map(d => ({
      key: String(d.name),
      source: 'smart' as const,
      isSortable: false,
      visible: true,
    })) ?? []),
    ...(traitAgg?.map(d => ({
      key: String(d.groupTitle),
      source: 'trait' as const,
      isSortable: false,
    })) ?? []),
    ...[
      ...Object.keys(leadDefaults?.custom ?? {}),
      ...(customKeyAgg?.map(d => String(d.key)).filter(Boolean) ?? []),
    ]
      .filter((k, i, arr) => arr.indexOf(k) === i)
      .map(key => ({
        key,
        source: 'custom' as const,
        isSortable: false,
      })),
  ]

  const _defaultColumns = defaultColumns.map(key => {
    return { ...columns.find(col => col.key === key), visible: true }
  }) as LeadTableColumn[]

  const otherColumns = columns.filter(c => !defaultColumns.some(key => key === c.key))

  return [..._defaultColumns, ...otherColumns].map(c => ({
    isEditable: false,
    width: 175,
    isResizable: true,
    visible: false,
    title: startCase(c.key),
    renderCell: getRenderCell(c, event, onColumnUpdate),
    renderHeader: getHeaderRender(c),
    ...c,
    ...defaultsByColumn[c.key],
  }))
}

const defaultColumns = [
  'temp',
  'name',
  'created',
  'email',
  'company',
  'title',
  'optIn',
  'followUp',
  'mediaStatus',
  'notes',
]

const defaultsByColumn: Record<string, Omit<LeadTableColumn, 'key' | 'source'>> = {
  temp: { width: 105, dataType: DataType.String, isResizable: false, alwaysSelected: true },
  name: { width: 175, dataType: DataType.String, alwaysSelected: true },
  created: { width: 120, dataType: DataType.Date },
  email: { width: 210, dataType: DataType.String },
  company: { width: 200, dataType: DataType.String },
  title: { width: 170, dataType: DataType.String },
  optIn: { width: 75, dataType: DataType.Boolean },
  followUp: { width: 105, dataType: DataType.Boolean },
  followUpOwner: { width: 160, dataType: DataType.String },
  notes: { width: 90, dataType: DataType.String },
  mediaStatus: { width: 130, dataType: DataType.Boolean },
}

function getHeaderRender(column: LeadTableColumn): LeadTableColumn['renderHeader'] {
  if (column.key === 'mediaStatus') {
    return () => <Text>Attachments</Text>
  }
  if (column.source === 'smart') {
    return () => (
      <Tooltip
        content={
          <Flex direction="column" align="center">
            <Text textStyle="bodyMedium">Smart Column</Text>
            <Text textStyle="bodyXSmall" fontWeight="normal" textAlign="center">
              This column was mapped from
              {column.key ? ` the original “${startCase(column.key)}” field.` : ' another field'}
            </Text>
          </Flex>
        }
      >
        <Flex gap={3} align="center">
          <Text>{startCase(column.key)}</Text>
          <SparklesIcon color="primary.500" fontSize="md" />
        </Flex>
      </Tooltip>
    )
  }
  return undefined
}

function getRenderCell(
  column: LeadTableColumn,
  event: IEvent | undefined,
  onColumnUpdate?: (updatedLead?: Lead & { id: string }) => void
): LeadTableColumn['renderCell'] {
  const isSmart = column.source === 'smart'
  if (isSmart) {
    return props => {
      if (props.value) return null
      return (
        <Text textStyle="bodySmall" opacity={0.7} as="i" color="red.500">
          No Match Found
        </Text>
      )
    }
  }

  switch (column.key) {
    case 'followUpOwner':
      return props => <FollowUpOwnerText {...props.rowData} />

    case 'phone':
      return props => <Text>{formatPhoneNumbers(props.value)}</Text>

    case 'momentId':
      return props => <Text>{event?.moments?.find(m => m.id === props.value)?.title}</Text>

    case 'name':
      return props => (
        <Text as={props.value ? 'span' : 'i'} opacity={props.value ? 1 : 0.5}>
          {(props.value ?? 'Anonymous Session') || 'Unnamed Lead'}
        </Text>
      )

    case 'notes':
      return props => {
        if (!props.value) return null

        return (
          <Tooltip content={props.value}>
            <NewspaperIcon color="black" type="outline" />
          </Tooltip>
        )
      }

    case 'mediaStatus':
      return props => {
        if (!props.value) return null

        const { eventId, id: sessionId, workspaceId, mediaStatus } = props.rowData
        if (!workspaceId || !eventId || !sessionId) return null

        return (
          <Flex onClick={e => e.stopPropagation()} px={3}>
            <LeadTableSessionMediaPreview
              eventId={eventId}
              sessionId={sessionId}
              workspaceId={workspaceId}
              mediaStatus={mediaStatus}
              onClick={() => {}}
            />
          </Flex>
        )
      }

    case 'visitCount':
      return props => {
        if (props.value > 1)
          return (
            <Flex direction="row" gap={2} align="center">
              <Text>{props.value}</Text>
              <ArrowPathIcon color="rgba(b190, 190, 190, 1)" fontSize="sm" />
            </Flex>
          )
        else return <Text>{props.value}</Text>
      }

    case 'temp':
      return props => {
        const leadId = props.rowData.leadId
        if (!leadId) return <Box h="40px" />

        return (
          <Tooltip content={startCase(props.value || 'unset')} positioning={{ placement: 'top' }}>
            {onColumnUpdate !== undefined ? (
              <Flex px={3} onClick={e => e.stopPropagation()}>
                <Menu
                  buttonLabel={<LeadTempIcon value={props.value as LeadTemperature} />}
                  options={[...leadTemperatures, null].map(key => ({
                    value: key || 'unset',
                    label: (
                      <Flex align="center" gap={3}>
                        <LeadTempIcon value={key as LeadTemperature} />
                        <Text textStyle="bodySmall" fontWeight="500">
                          {startCase(key || 'unset')}
                        </Text>
                      </Flex>
                    ),
                  }))}
                  variant="ghost"
                  value={props.value || 'unset'}
                  onChange={value =>
                    onColumnUpdate?.({
                      id: leadId,
                      temp: value === 'unset' ? undefined : (value as LeadTemperature),
                    })
                  }
                  as={IconButton}
                  portal
                />
              </Flex>
            ) : (
              <Flex align="center" h="40px">
                <LeadTempIcon value={props.value as LeadTemperature} />
              </Flex>
            )}
          </Tooltip>
        )
      }
  }
}

function FollowUpOwnerText({ followUpGroup, followUpUserId }: any) {
  const user = useUser(followUpUserId)
  return <Text>{user?.name || followUpGroup}</Text>
}

export function useNewSessionCount() {
  const { event } = useEvent()
  const [newCount, setNewCount] = useState(0)

  const count = event?.sessionCount || 0

  const lastCount = useRef(0)
  useEffect(() => {
    if (!count) return
    if (!lastCount.current || lastCount.current >= count) {
      lastCount.current = count
      return
    }

    lastCount.current = count
    setNewCount(s => s + 1)
    const timer = setTimeout(() => setNewCount(0), 5000)
    return () => clearTimeout(timer)
  }, [count])

  return newCount
}

export function formatPhoneNumbers(phone?: string) {
  if (!phone) return phone
  const digits = phone.replace(/\D/g, '')
  if (!digits) return phone
  if (digits.length === 10)
    // US format: (123) 456-7890
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  else if (digits.length === 11 && digits.startsWith('1'))
    // US with country code: +1 (123) 456-7890
    return digits.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4')
  else if (digits.length === 11)
    // International 11 digits: +XX XXX XXX XXXX
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4')
  else if (digits.length === 12)
    // International 12 digits: +XXX XXX XXX XXXX
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4')
  else if (digits.length === 13)
    // International 13 digits: +XXXX XXX XXX XXXX
    return digits.replace(/(\d{4})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4')
  else if (digits.length >= 7 && digits.length <= 15) {
    const groups = []
    let remaining = digits

    while (remaining.length > 3) {
      groups.push(remaining.slice(0, 3))
      remaining = remaining.slice(3)
    }

    if (remaining.length > 0) groups.push(remaining)

    return '+' + groups.join(' ')
  } else return digits.replace(/(\d{3})(?=\d)/g, '$1 ').replace(/^/, '+')
}

export async function getSessionMedia(
  workspaceId: string,
  sessionId: string,
  eventId?: string
): Promise<{ path: string; url: string; meta: FullMetadata }[]> {
  const path = eventId
    ? `Workspaces/${workspaceId}/events/${eventId}/sessions/${sessionId}`
    : `Workspaces/${workspaceId}/sessions/${sessionId}`
  const mediaUrls = await getStorageUrlsInPath(path)
  return mediaUrls
}

export function cleanDcKey(string: string) {
  let str = string
  str = str.replace(/(?:^\d+|[\x00-\x1F"'\\])/g, '')

  // TODO: dataconnect gql doesn't support quoted keys, so move away from gql to standard cloud sql updates to support quoted keys
  str = str.replace(/ /g, '_') // replace spaces with underscores
  str = str.replace(/[^a-zA-Z\d_]/g, '') // remove non-alphanumeric characters
  str = str.replace(/^\d+/, '') // remove leading numbers
  return str.trim()
}
